import React, { useEffect, useState, useCallback } from 'react';
import { playbookApi } from '../api/playbookApi';
import type { PlaybookEntity } from '../types/playbook.types';

interface PlaybookUsageTabProps {
  playbook: PlaybookEntity;
}

const PlaybookUsageTab: React.FC<PlaybookUsageTabProps> = ({ playbook }) => {
  const [loading, setLoading] = useState(false);

  const loadUsage = useCallback(async () => {
    setLoading(true);
    try {
      await playbookApi.getAnalyticsOverview(playbook.id);
    } catch {
      // Analytics not available yet — silent
    } finally {
      setLoading(false);
    }
  }, [playbook.id]);

  useEffect(() => {
    loadUsage();
  }, [loadUsage]);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-3 text-center">
          <div className="text-2xl font-bold text-gray-900 dark:text-white">
            {playbook.statuses?.length || 0}
          </div>
          <div className="text-[10px] text-gray-500 dark:text-gray-400 uppercase">Statuses</div>
        </div>
        <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-3 text-center">
          <div className="text-2xl font-bold text-gray-900 dark:text-white">
            {playbook.transitions?.length || 0}
          </div>
          <div className="text-[10px] text-gray-500 dark:text-gray-400 uppercase">Transitions</div>
        </div>
        <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-3 text-center">
          <div className="text-2xl font-bold text-gray-900 dark:text-white">v{playbook.version}</div>
          <div className="text-[10px] text-gray-500 dark:text-gray-400 uppercase">Version</div>
        </div>
        <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-3 text-center">
          <div className="text-2xl font-bold text-gray-900 dark:text-white">
            {playbook.practice_areas?.length || 0}
          </div>
          <div className="text-[10px] text-gray-500 dark:text-gray-400 uppercase">Practice Areas</div>
        </div>
      </div>

      {/* Practice areas & jurisdictions */}
      {(playbook.practice_areas?.length > 0 || playbook.jurisdiction_codes?.length > 0) && (
        <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-3 space-y-2">
          {playbook.practice_areas?.length > 0 && (
            <div>
              <span className="text-[10px] font-medium text-gray-500 dark:text-gray-400 uppercase">Practice Areas</span>
              <div className="flex flex-wrap gap-1 mt-1">
                {playbook.practice_areas.map((pa) => (
                  <span key={pa} className="rounded-full bg-lex-accent-soft px-2 py-0.5 text-[10px] font-medium text-lex-accent">
                    {pa}
                  </span>
                ))}
              </div>
            </div>
          )}
          {playbook.jurisdiction_codes?.length > 0 && (
            <div>
              <span className="text-[10px] font-medium text-gray-500 dark:text-gray-400 uppercase">Jurisdictions</span>
              <div className="flex flex-wrap gap-1 mt-1">
                {playbook.jurisdiction_codes.map((jc) => (
                  <span key={jc} className="rounded-full bg-teal-50 px-2 py-0.5 text-[10px] font-medium text-teal-700 dark:bg-teal-900/30 dark:text-teal-300">
                    {jc}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {loading && (
        <div className="flex items-center py-4">
          <div className="h-5 w-5 animate-spin rounded-full border-2 border-lex-accent border-t-transparent" />
          <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">Loading usage data...</span>
        </div>
      )}
    </div>
  );
};

export default PlaybookUsageTab;
